import { Lock, Network, ShieldAlert } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ConnectionMode, VpnProtocol } from '@/types/provision';

/** Roles the wizard can provision end-to-end (SSH → preflight → deploy). */
export type ProvisionRole = 'vpn_server' | 'v2ray_edge' | 'proxy_ingress' | 'bootstrap_edge';

/** Mutable state collected across every wizard step. */
export interface WizardForm {
  role: ProvisionRole;
  name: string;
  provider: string;
  vm_type: string;
  public_ip: string;
  private_ip: string;
  ssh_username: string;
  ssh_password: string;
  connection_mode: ConnectionMode;
  protocol: VpnProtocol;
  /** proxy_ingress only — Cloudflare-fronted DNS name the HTTPS-CONNECT proxy serves on. */
  proxy_hostname: string;
  region_continent: string;
  region_country: string;
  region_city: string;
}

export const EMPTY_WIZARD_FORM: WizardForm = {
  role: 'vpn_server',
  name: '',
  provider: '',
  vm_type: '',
  public_ip: '',
  private_ip: '',
  ssh_username: '',
  ssh_password: '',
  connection_mode: 'standard',
  // AmneziaWG is the platform's only client tunnel protocol — see VpnProtocol.
  protocol: 'amneziawg',
  proxy_hostname: '',
  region_continent: '',
  region_country: '',
  region_city: '',
};

const ROLE_OPTIONS: { value: ProvisionRole; label: string; blurb: string }[] = [
  {
    value: 'vpn_server',
    label: 'VPN server',
    blurb: 'AmneziaWG exit for normal clients.',
  },
  {
    value: 'v2ray_edge',
    label: 'Reality edge',
    blurb: 'VLESS-Reality stealth edge (Xray) for high-censorship regions.',
  },
  {
    value: 'proxy_ingress',
    label: 'Proxy ingress',
    blurb: 'HTTPS-CONNECT proxy for browser extensions (AmneziaWG-backed).',
  },
  {
    value: 'bootstrap_edge',
    label: 'Bootstrap edge',
    blurb: 'VLESS-Reality transport to the API origin — censored-region control-channel bootstrap.',
  },
];

interface StepConnectionProps {
  form: WizardForm;
  onChange: (patch: Partial<WizardForm>) => void;
}

interface TogglePillProps<T extends string> {
  value: T;
  options: { label: string; value: T }[];
  onChange: (v: T) => void;
}

function TogglePills<T extends string>({ value, options, onChange }: TogglePillProps<T>): JSX.Element {
  return (
    <div className="flex gap-1 rounded-md border border-input bg-muted/30 p-0.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={[
            'flex-1 rounded px-3 py-1 text-xs font-medium transition-colors',
            value === opt.value
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          ].join(' ')}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

const MODE_OPTIONS: { label: string; value: ConnectionMode }[] = [
  { label: 'Standard', value: 'standard' },
  { label: 'P2P', value: 'p2p' },
];

/**
 * Protocols a new VPN server may be registered with. WireGuard and OpenVPN are
 * intentionally not offered — the platform serves neither (see VpnProtocol),
 * and the fleet API rejects both with a 400.
 */
const PROTOCOL_OPTIONS: { label: string; value: VpnProtocol }[] = [
  { label: 'AmneziaWG', value: 'amneziawg' },
  { label: 'Tor', value: 'tor' },
  { label: 'Mixnet', value: 'mixnet' },
];

/** Step 1 — Identity, Network, and SSH credentials grouped into bordered sections. */
export function StepConnection({ form, onChange }: StepConnectionProps): JSX.Element {
  return (
    <div className="space-y-4">
      {/* Role — the headline choice; drives which fields apply below. */}
      <section className="rounded-md border p-4 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          What are you provisioning?
        </p>
        <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Server role">
          {ROLE_OPTIONS.map((opt) => {
            const selected = form.role === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => onChange({ role: opt.value })}
                className={[
                  'rounded-md border p-3 text-left transition-colors',
                  selected
                    ? 'border-primary bg-primary/10 ring-1 ring-primary/30'
                    : 'border-border bg-card hover:border-primary/40 hover:bg-accent',
                ].join(' ')}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">{opt.label}</span>
                  <span className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                    {opt.value}
                  </span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{opt.blurb}</p>
              </button>
            );
          })}
        </div>
      </section>

      {/* Identity */}
      <section className="rounded-md border p-4 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Identity</p>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label htmlFor="w-name">Server name</Label>
            <Input
              id="w-name"
              value={form.name}
              onChange={(e) => onChange({ name: e.target.value })}
              placeholder="vpn-fra-01"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="w-provider">Provider</Label>
            <Input
              id="w-provider"
              value={form.provider}
              onChange={(e) => onChange({ provider: e.target.value })}
              placeholder="hetzner"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="w-vm-type">VM type</Label>
            <Input
              id="w-vm-type"
              value={form.vm_type}
              onChange={(e) => onChange({ vm_type: e.target.value })}
              placeholder="cx21"
            />
          </div>
        </div>
      </section>

      {/* Network */}
      <section className="rounded-md border p-4 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Network</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="w-public-ip">Public IP</Label>
            <Input
              id="w-public-ip"
              value={form.public_ip}
              onChange={(e) => onChange({ public_ip: e.target.value })}
              placeholder="203.0.113.10"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="w-private-ip">Private IP <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input
              id="w-private-ip"
              value={form.private_ip}
              onChange={(e) => onChange({ private_ip: e.target.value })}
              placeholder="10.0.0.1"
            />
          </div>
        </div>
        {/* proxy_ingress serves HTTPS-CONNECT on :443 with a Let's Encrypt cert,
            so it needs a DNS name (Cloudflare-fronted) pointing at this IP. */}
        {form.role === 'proxy_ingress' && (
          <div className="space-y-1">
            <Label htmlFor="w-proxy-hostname">Proxy hostname</Label>
            <Input
              id="w-proxy-hostname"
              value={form.proxy_hostname}
              onChange={(e) => onChange({ proxy_hostname: e.target.value })}
              placeholder="proxy-eu.getpocketvpn.com"
            />
          </div>
        )}

        {/* Mode + protocol apply only to a VPN server. A Reality edge runs
            Xray/VLESS-Reality; its transport config is generated server-side
            by the deploy, so there's nothing to choose here. */}
        {form.role === 'vpn_server' && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Mode</Label>
              <TogglePills
                value={form.connection_mode}
                options={MODE_OPTIONS}
                onChange={(v) => onChange({ connection_mode: v })}
              />
            </div>
            <div className="space-y-1">
              <Label>Protocol</Label>
              <TogglePills
                value={form.protocol}
                options={PROTOCOL_OPTIONS}
                onChange={(v) => onChange({ protocol: v })}
              />
            </div>
          </div>
        )}

        {form.role === 'v2ray_edge' && (
          <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-foreground">
            <Network className="h-4 w-4 shrink-0 mt-0.5 text-primary" aria-hidden />
            <span>
              Provision installs Xray and configures a VLESS-Reality stealth edge. The Reality
              keys, short-id and masquerade SNI are generated on the host during deploy — no
              transport settings to pick here.
            </span>
          </div>
        )}

        {form.role === 'proxy_ingress' && (
          <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-foreground">
            <Network className="h-4 w-4 shrink-0 mt-0.5 text-primary" aria-hidden />
            <span>
              Provision installs nginx + 3proxy serving HTTPS-CONNECT on :443 (the transport
              browser extensions use). Egress goes out this host directly unless a shared
              AmneziaWG exit is wired in the controller vault. Point the hostname's DNS at this IP
              first so the Let's Encrypt cert can issue.
            </span>
          </div>
        )}

        {form.role === 'vpn_server' && form.protocol === 'tor' && (
          <div className="flex items-start gap-2 rounded-md border border-purple-200 bg-purple-50/60 dark:border-purple-800 dark:bg-purple-950/20 px-3 py-2 text-sm text-purple-800 dark:text-purple-300">
            <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
            <span>
              VPN traffic will be routed through the Tor network. The preflight will require
              ≥1.5 GB RAM and ≥15 GB disk. Provision installs and configures the Tor daemon
              with iptables transparent-proxy rules.
            </span>
          </div>
        )}

        {form.role === 'vpn_server' && form.protocol === 'mixnet' && (
          <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50/60 dark:border-blue-800 dark:bg-blue-950/20 px-3 py-2 text-sm text-blue-800 dark:text-blue-300">
            <Network className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
            <span>
              VPN traffic will be routed through the Nym mixnet — resistant to traffic
              analysis attacks. The preflight will require ≥2 GB RAM and ≥20 GB disk.
              Provision installs the Nym gateway daemon as a mixnet entry point.
            </span>
          </div>
        )}
      </section>

      {/* SSH access */}
      <section className="rounded-md border border-indigo-200 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/20 p-4 space-y-3">
        <div className="flex items-center gap-1.5">
          <Lock className="h-3 w-3 text-indigo-500" aria-hidden />
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400">SSH access</p>
          <span className="ml-auto text-xs text-muted-foreground">encrypted at rest</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="w-ssh-user">Username</Label>
            <Input
              id="w-ssh-user"
              value={form.ssh_username}
              onChange={(e) => onChange({ ssh_username: e.target.value })}
              placeholder="root"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="w-ssh-pass">Password</Label>
            <Input
              id="w-ssh-pass"
              type="password"
              value={form.ssh_password}
              onChange={(e) => onChange({ ssh_password: e.target.value })}
              placeholder="••••••••"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
